import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { browserTimezone } from "@/lib/datetime";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, object = signed in
    const [error, setError] = useState("");
    const [orgTimezone, setOrgTimezone] = useState(browserTimezone());

    const loadSettings = useCallback(async () => {
        try {
            const { data } = await api.get("/settings");
            if (data?.timezone) setOrgTimezone(data.timezone);
        } catch { /* ignore */ }
    }, []);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            if (data?.org_id) loadSettings();
        } catch {
            setUser(false);
        }
    }, [loadSettings]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data);
            if (data?.org_id) loadSettings();
            return data;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const register = async (payload) => {
        setError("");
        try {
            const { data } = await api.post("/auth/register", payload);
            setUser(data);
            if (data?.org_id) loadSettings();
            return data;
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            setError(msg);
            throw new Error(msg);
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_e) {}
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, error, login, register, logout, refresh, orgTimezone, setOrgTimezone }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
